package one.healthium.bland.presentation.navigation.root

import co.touchlab.kermit.Logger
import com.arkivanov.decompose.ComponentContext
import com.arkivanov.decompose.DecomposeExperimentFlags
import com.arkivanov.decompose.router.slot.ChildSlot
import com.arkivanov.decompose.router.slot.SlotNavigation
import com.arkivanov.decompose.router.slot.activate
import com.arkivanov.decompose.router.slot.child
import com.arkivanov.decompose.router.slot.childSlot
import com.arkivanov.decompose.router.slot.dismiss
import com.arkivanov.decompose.router.stack.ChildStack
import com.arkivanov.decompose.router.stack.StackNavigation
import com.arkivanov.decompose.router.stack.active
import com.arkivanov.decompose.router.stack.childStack
import com.arkivanov.decompose.router.stack.childStackWebNavigation
import com.arkivanov.decompose.router.stack.pop
import com.arkivanov.decompose.router.stack.pushNew
import com.arkivanov.decompose.router.stack.replaceAll
import com.arkivanov.decompose.router.webhistory.WebNavigation
import com.arkivanov.decompose.value.Value
import com.arkivanov.essenty.lifecycle.Lifecycle
import com.arkivanov.essenty.lifecycle.coroutines.coroutineScope
import com.arkivanov.essenty.lifecycle.coroutines.repeatOnLifecycle
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.launch
import kotlinx.serialization.Serializable
import one.healthium.base.launcher.PlatformLauncher
import one.healthium.base.preferences.AppPreferences
import one.healthium.bland.AppLaunchState
import one.healthium.bland.presentation.navigation.main.DefaultMainComponent
import one.healthium.decompose.waitForChild
import one.healthium.domain.paywall.models.PaywallSet
import one.healthium.domain.user.UserModel
import one.healthium.domain.user.models.SubscriptionStatus
import one.healthium.login.navigation.login.DefaultSignInComponent
import one.healthium.login.navigation.silent_login.DefaultSilentLoginComponent
import one.healthium.onboarding.domain.OnboardingModel
import one.healthium.onboarding.navigation.DefaultOnboardingComponent
import one.healthium.paywall.navigation.DefaultPaywallComponent
import one.primehealth.app.DeepLink
import one.primehealth.app.DeepLinkChannel
import org.koin.core.component.KoinComponent
import org.koin.core.component.inject

data class RootComponentParams(
    val platformLauncher: PlatformLauncher,
    val appLaunchState: AppLaunchState,
    val appPreferences: AppPreferences,
)

class DefaultRootComponent(
    componentContext: ComponentContext,
    private val params: RootComponentParams,
    private val initialDeepLink: () -> String?,
) : RootComponent, ComponentContext by componentContext, KoinComponent {

    private val paywallController = PaywallController()

    private val scope = coroutineScope(Dispatchers.Main.immediate + SupervisorJob())

    private val nav = StackNavigation<Config>()

    private val deepLinkChannel by inject<DeepLinkChannel>()

    private var appLaunchState: AppLaunchState = params.appLaunchState

    private val _stack = childStack(
        source = nav,
        serializer = Config.serializer(),
        initialStack = { getInitialStack(initialDeepLink(), appLaunchState) },
        childFactory = ::child,
        handleBackButton = true,
    )

    override val webNavigation: WebNavigation<*> =
        childStackWebNavigation(
            navigator = nav,
            stack = _stack,
            serializer = Config.serializer(),
            childSelector = {
                when (val child = it.instance) {
                    is RootComponent.Child.Main -> child.component
                    is RootComponent.Child.Onboarding -> child.component
                    is RootComponent.Child.Paywall -> null
                    is RootComponent.Child.SignIn -> null
                    is RootComponent.Child.SilentLogin -> null
                }
            },
        )

    private val slotNav = SlotNavigation<Config>()

    private val _slot = childSlot(
        source = slotNav,
        serializer = Config.serializer(),
        childFactory = { config, componentContext ->
            child(config, componentContext)
        }
    )

    private var unhandledDeepLink: DeepLink? = null

    init {
        DecomposeExperimentFlags.duplicateConfigurationsEnabled = true
        scope.launch {
            repeatOnLifecycle(Lifecycle.State.RESUMED) {
                deepLinkChannel.channel.receiveAsFlow().collect { deepLink ->
                    onDeepLink(deepLink)
                }
            }
        }
        scope.launch {
            repeatOnLifecycle(Lifecycle.State.RESUMED) {
                if (_stack.active.instance is RootComponent.Child.Main &&
                    appLaunchState.user.purchases.subscription == SubscriptionStatus.Inactive
                ) {
                    val paywallSet = appLaunchState.paywallSet
                    if (paywallSet != null) {
                        delay(500)
                        slotNav.activate(Config.Paywall())
                    }
                }
            }
        }
        if (unhandledDeepLink != null) {
            handleDeepLink(unhandledDeepLink!!)
        }
    }

    override val stack: Value<ChildStack<*, RootComponent.Child>> = _stack

    override val slot: Value<ChildSlot<*, RootComponent.Child>> = _slot

    override fun onBackClicked() {
        nav.pop()
    }

    override fun onDeepLink(deepLink: String) {
        val parsed = DeepLink.parse(deepLink)
        if (parsed != null) {
            handleDeepLink(parsed)
        }
    }

    override fun onDeepLink(deepLink: DeepLink) {
        handleDeepLink(deepLink)
    }

    override fun onRefreshStack(appLaunchState: AppLaunchState) {
        this.appLaunchState = appLaunchState
        nav.replaceAll(*getInitialStack(null, appLaunchState).toTypedArray())
    }

    private fun handleDeepLink(deepLink: DeepLink) {
        Logger.d("onDeepLink: deepLink=$deepLink")
        when (deepLink) {
            is DeepLink.Paywall -> slotNav.activate(Config.Paywall(deepLink.id))
            is DeepLink.SilentLogin -> slotNav.activate(Config.SilentLogin(deepLink.token))
            else -> {
                // TODO check if the user authenticated?
                _stack.waitForChild<RootComponent.Child.Main> { child ->
                    child.component.onDeepLink(deepLink)
                }
            }
        }
    }

    private fun child(config: Config, componentContext: ComponentContext): RootComponent.Child =
        when (config) {
            is Config.Onboarding -> RootComponent.Child.Onboarding(
                DefaultOnboardingComponent(
                    componentContext = componentContext,
                    appPreferences = params.appPreferences,
                    model = config.model,
                    paywallSet = config.paywallSet,
                    onSignInClicked = ::onSignInClicked,
                    onFinished = ::onOnboardingFinished,
                )
            )

            Config.SignIn -> RootComponent.Child.SignIn(
                DefaultSignInComponent(
                    componentContext = componentContext,
                    onBackClicked = {
                        if (_slot.child?.instance is RootComponent.Child.SignIn) {
                            slotNav.dismiss()
                        } else {
                            onBackClicked()
                        }
                    },
                    onUserSignedIn = ::onUserSignedIn,
                )
            )

            Config.Main -> RootComponent.Child.Main(
                DefaultMainComponent(
                    componentContext = componentContext,
                    paywallController = paywallController,
                    platformLauncher = params.platformLauncher,
                )
            )

            is Config.Paywall -> RootComponent.Child.Paywall(
                DefaultPaywallComponent(
                    componentContext = componentContext,
                    paywallId = config.paywallId,
                    onPaywallPassed = ::onPaywallFinished,
                    onSignInClicked = ::onSignInModalClicked,
                )
            )

            is Config.SilentLogin -> RootComponent.Child.SilentLogin(
                DefaultSilentLoginComponent(
                    componentContext = componentContext,
                    token = config.token,
                    onUserSignedIn = ::onUserSignedIn,
                    onDeclined = ::onSilentLoginDeclined,
                )
            )
        }

    private fun getInitialStack(link: String?, appLaunchState: AppLaunchState): List<Config> {
        val deepLink = link?.let { DeepLink.parse(it) } ?: deepLinkChannel.channel.tryReceive().getOrNull()
        Logger.d("getInitialStack: deepLink=$deepLink")
        if (deepLink != null) {
            val stack = when (deepLink) {
                is DeepLink.Paywall -> listOf(Config.Paywall(deepLink.id))
                is DeepLink.SilentLogin -> listOf(Config.SilentLogin(deepLink.token))

                DeepLink.DailyTasks,
                is DeepLink.Insight,
                DeepLink.Insights,
                is DeepLink.MealPlan,
                is DeepLink.Recipe,
                DeepLink.Today,
                is DeepLink.Workout -> {
                    if (appLaunchState.user.purchases.subscription == SubscriptionStatus.Active) {
                        unhandledDeepLink = deepLink
                        listOf(Config.Main)
                    } else {
                        null
                    }
                }
            }

            if (stack != null) {
                return stack
            }
        }

        return when (appLaunchState.user.purchases.subscription) {
            SubscriptionStatus.Active -> {
                listOf(Config.Main)
            }

            SubscriptionStatus.Inactive -> {
                if (appLaunchState.onboarding != null) {
                    listOf(Config.Onboarding(appLaunchState.onboarding, appLaunchState.paywallSet))
                } else if (appLaunchState.paywallSet != null) {
                    listOf(Config.Paywall())
                } else {
                    listOf()
                }
            }
        }
    }

    private fun onSignInClicked() {
        nav.pushNew(Config.SignIn)
    }

    private fun onSignInModalClicked() {
        slotNav.activate(Config.SignIn)
    }

    private fun onOnboardingFinished() {
        val appLaunchState = appLaunchState
        if (appLaunchState.user.purchases.subscription == SubscriptionStatus.Inactive) {
            attachPaywall()
        } else {
            nav.replaceAll(Config.Main)
        }
    }

    private fun onUserSignedIn(user: UserModel) {
        if (_slot.child?.instance is RootComponent.Child.SilentLogin) {
            slotNav.dismiss()
        }
        if (_slot.child?.instance is RootComponent.Child.SignIn) {
            slotNav.dismiss()
        }
        when (user.purchases.subscription) {
            SubscriptionStatus.Active -> {
                nav.replaceAll(Config.Main)
            }

            SubscriptionStatus.Inactive -> {
                attachPaywall()
            }
        }
    }

    private fun attachPaywall() {
        val paywallSet = appLaunchState.paywallSet
        if (paywallSet != null) {
            paywallController.setActive(true)
            nav.replaceAll(Config.Main)
            scope.launch {
                if (paywallSet.config.delay) {
                    delay((paywallSet.config.delayTimeout * 1000).toLong())
                }
                slotNav.activate(Config.Paywall())
            }
        }
    }

    private fun onPaywallFinished() {
        paywallController.setActive(false)
        if (_slot.child?.instance is RootComponent.Child.Paywall) {
            slotNav.dismiss()
        } else {
            nav.replaceAll(Config.Main)
        }
    }

    private fun onSilentLoginDeclined() {
        if (_slot.child?.instance is RootComponent.Child.SilentLogin) {
            slotNav.dismiss()
        } else {
            // in this case we started silent login flow from initialDeeplink and therefore as standalone screen
            // so in case of error we have to redirect user to initial stack
            nav.replaceAll(*getInitialStack(null, appLaunchState).toTypedArray())
        }
    }

    @Serializable
    private sealed interface Config {
        @Serializable
        data class Onboarding(
            val model: OnboardingModel,
            val paywallSet: PaywallSet?,
        ) : Config

        @Serializable
        data object SignIn : Config

        @Serializable
        data object Main : Config

        @Serializable
        data class Paywall(
            val paywallId: String? = null,
        ) : Config

        @Serializable
        data class SilentLogin(
            val token: String,
        ) : Config
    }
}